<script setup lang="ts">
import { nextTick, onMounted, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const eventSlug = route.params['eventSlug'] as string

const { event, fetchEvent } = useEvents()

const tabs = [
  { name: 'organizer.events.settings.general', label: () => t('events.settings.tab_general') },
  { name: 'organizer.events.settings.logo', label: () => t('events.settings.tab_logo') },
  { name: 'organizer.events.settings.email', label: () => t('events.settings.tab_email') },
]

// On narrow screens the tab row scrolls horizontally on its own (see the
// wrapping overflow-x-auto container in the template) instead of pushing the
// whole page into a sideways scroll. Keep the active tab visible whenever the
// route changes, including on initial mount, in case it starts off-screen.
const tabRefs = new Map<string, HTMLElement>()

function setTabRef(name: string, el: Element | ComponentPublicInstance | null): void {
  // RouterLink is a component: the ref resolves to its public instance, whose
  // $el is the rendered <a>. Handle both that and a plain Element defensively.
  const node = el && '$el' in el ? (el as ComponentPublicInstance).$el as unknown : el
  if (node instanceof HTMLElement) {
    tabRefs.set(name, node)
  } else {
    tabRefs.delete(name)
  }
}

function scrollActiveTabIntoView(): void {
  const el = tabRefs.get(route.name as string)
  // jsdom (used by Vitest) does not implement scrollIntoView, guard defensively.
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}

onMounted(() => {
  void fetchEvent(eventSlug)
  scrollActiveTabIntoView()
})
watch(() => route.name, () => void nextTick(scrollActiveTabIntoView))
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ t('events.settings.heading') }}</span>
      </nav>

      <!-- Heading -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-white">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-500">{{ t('events.settings.heading') }}</span>
        </h1>
        <p class="text-slate-400 mt-1">{{ t('events.settings.heading') }}</p>
      </div>

      <!-- Tab bar -->
      <div class="overflow-x-auto mb-6">
        <div class="flex gap-1 border-b border-slate-700">
          <RouterLink
            v-for="tab in tabs"
            :key="tab.name"
            :ref="(el) => setTabRef(tab.name, el)"
            :to="{ name: tab.name }"
            class="px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap shrink-0"
            :class="
              route.name === tab.name
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            "
          >
            {{ tab.label() }}
          </RouterLink>
        </div>
      </div>

      <!-- Panel content -->
      <RouterView />
    </main>
  </OrganizerLayout>
</template>
